import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Divider,
  useTheme,
  alpha,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar,
  Badge,
} from '@mui/material';
import {
  LocationOn,
  LocalShipping,
  Warehouse,
  Science,
  ArrowForward,
  ArrowRightAlt,
  CheckCircle,
  Schedule,
  Refresh,
  TrendingUp,
  Inventory2,
  FlightTakeoff,
  FlightLand,
  History,
  Speed,
  PrecisionManufacturing,
  Timeline,
  Hub,
} from '@mui/icons-material';
import api from '../services/api';

// Types
interface Location {
  id: string;
  code: string;
  name: string;
  type: 'SATELLITE' | 'MAIN_LAB';
  capabilities: string[];
  api_endpoint: string;
  is_active: boolean;
}

interface Transfer {
  id: string;
  transfer_number: string;
  batch_number: string;
  sample_count: number;
  status: 'PENDING' | 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';
  from_location_code: string;
  to_location_code: string;
  sent_at: string;
  sent_by_name: string;
  received_at?: string;
  received_by_name?: string;
}

interface TransferStats {
  totalSent: number;
  totalReceived: number;
  pendingOut: number;
  pendingIn: number;
  averageTransitHours: number | null;
}

interface SampleCounts {
  reception: number;
  prepMecanique: number;
  enTransit: number;
  icpPrepChimique: number;
  auPrepChimique: number;
  saaPrepChimique: number;
  icpAnalyse: number;
  auAnalyse: number;
  saaAnalyse: number;
  validation: number;
  pret: number;
}

const MultiSiteDashboard: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [transferStats, setTransferStats] = useState<TransferStats | null>(null);
  const [sampleCounts, setSampleCounts] = useState<SampleCounts | null>(null);
  const [locationStats, setLocationStats] = useState<Array<{
    id: string;
    code: string;
    name: string;
    type: string;
    stats: any;
    online: boolean;
    error?: string;
  }>>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch multi-site statistics (aggregated from all locations)
      const multiSiteRes = await api.get(`/locations/multi-site/stats?_=${Date.now()}`);
      console.log('🌐 Multi-Site Stats:', multiSiteRes);

      const multiSiteData = multiSiteRes || {};
      const locationsData = multiSiteData.locations || [];
      const aggregated = multiSiteData.aggregated || { par_statut: {}, total: 0, transfers: {} };

      // Extract location info for display
      const locationInfo = locationsData.map((loc: any) => ({
        id: loc.id,
        code: loc.code,
        name: loc.name,
        type: loc.type,
        capabilities: [],
        api_endpoint: '',
        is_active: true,
      }));
      setLocations(locationInfo);
      setLocationStats(locationsData); // Store location-specific stats
      console.log('📍 Locations loaded:', locationInfo.length, 'with stats');

      // Use aggregated statistics from all locations
      const byStatus = aggregated.par_statut || {};
      console.log('📊 Aggregated statistics:', {
        byStatus,
        total: aggregated.total,
        transfers: aggregated.transfers,
      });

      // Map aggregated statistics to counts
      const counts = {
        reception: Number(byStatus?.RECEPTION) || 0,
        prepMecanique: Number(byStatus?.PREPARATION_MECANIQUE) || 0,
        enTransit: Number(byStatus?.EN_COURS_ACHEMINEMENT) || 0,
        icpPrepChimique: Number(byStatus?.LABORATOIRE_ICP_PREP_CHIMIQUE) || 0,
        auPrepChimique: Number(byStatus?.LABORATOIRE_AU_PREP_CHIMIQUE) || 0,
        saaPrepChimique: Number(byStatus?.LABORATOIRE_SAA_PREP_CHIMIQUE) || 0,
        icpAnalyse: Number(byStatus?.LABORATOIRE_ICP_ANALYSE) || 0,
        auAnalyse: Number(byStatus?.LABORATOIRE_AU_ANALYSE) || 0,
        saaAnalyse: Number(byStatus?.LABORATOIRE_SAA_ANALYSE) || 0,
        validation: Number(byStatus?.VALIDATION_SERVICE) || 0,
        pret: Number(byStatus?.PRET) || 0,
      };
      console.log('📈 Sample counts set:', counts);
      setSampleCounts(counts);

      // Set transfer stats from aggregated data
      setTransferStats({
        totalSent: aggregated.transfers?.total || 0,
        totalReceived: 0, // Will be calculated from history
        pendingOut: aggregated.transfers?.pending || 0,
        pendingIn: aggregated.transfers?.inTransit || 0,
        averageTransitHours: null,
      });

      // Try to fetch transfer history from main lab
      try {
        const mainLabLocation = locationInfo.find((l: Location) => l.type === 'MAIN_LAB');
        if (mainLabLocation) {
          const historyRes = await api.get(`/transfers/history/${mainLabLocation.id}?limit=10`).catch((err) => {
            console.error('❌ Transfer history error:', err);
            return { data: [] };
          });
          console.log('📦 Transfers loaded:', historyRes.data?.length || 0);
          setTransfers(historyRes.data || []);
        }
      } catch (e) {
        console.error('❌ Transfer endpoints error:', e);
        setTransfers([]);
      }

      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('❌ Multi-Site fetch error:', err);
      setError(err.message || 'Failed to load multi-site data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RECEIVED': return 'success';
      case 'IN_TRANSIT': return 'warning';
      case 'PENDING': return 'info';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const satelliteLocation = locations.find(l => l.type === 'SATELLITE');
  const mainLabLocation = locations.find(l => l.type === 'MAIN_LAB');
  
  // Get location-specific stats
  const satelliteStats = locationStats.find((s: any) => s.type === 'SATELLITE');
  const mainLabStats = locationStats.find((s: any) => s.type === 'MAIN_LAB');
  
  // Extract counts from location-specific stats
  const satelliteCounts = satelliteStats?.stats?.par_statut || {};
  const mainLabCounts = mainLabStats?.stats?.par_statut || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            🌐 Multi-Site Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visualisation du flux d'échantillons entre les sites • Dernière MAJ: {lastRefresh.toLocaleTimeString('fr-FR')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
          onClick={fetchData}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Visual Workflow */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timeline /> Flux de Travail Inter-Sites
        </Typography>
        
        <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
          {/* Satellite Site */}
          <Grid item xs={12} md={4}>
            <Card 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                border: `2px solid ${theme.palette.warning.main}`,
                background: alpha(theme.palette.warning.main, 0.05),
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 56, height: 56 }}>
                    <Warehouse sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {satelliteLocation?.name || 'Site Satellite'}
                    </Typography>
                    <Chip 
                      label="SATELLITE" 
                      size="small" 
                      color="warning"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {Number(satelliteCounts?.RECEPTION) || 0}
                      </Typography>
                      <Typography variant="caption">Réception</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="secondary.main">
                        {Number(satelliteCounts?.PREPARATION_MECANIQUE) || 0}
                      </Typography>
                      <Typography variant="caption">Prép. Méca.</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" /> {satelliteLocation?.api_endpoint || 'Non configuré'}
                    </Typography>
                    <Chip 
                      label={satelliteStats?.online ? '🟢 En ligne' : '🔴 Hors ligne'} 
                      size="small"
                      color={satelliteStats?.online ? 'success' : 'error'}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                  {satelliteStats?.error && (
                    <Typography variant="caption" color="error">
                      {satelliteStats.error}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Transfer Arrow */}
          <Grid item xs={12} md={2}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Badge 
                  badgeContent={sampleCounts?.enTransit || 0} 
                  color="warning"
                  max={999}
                  sx={{ '& .MuiBadge-badge': { fontSize: '1rem', height: 28, minWidth: 28 } }}
                >
                  <LocalShipping 
                    sx={{ 
                      fontSize: 48, 
                      color: sampleCounts?.enTransit ? theme.palette.warning.main : theme.palette.grey[400],
                      animation: sampleCounts?.enTransit ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    }} 
                  />
                </Badge>
                <Typography variant="body2" fontWeight="bold" color="text.secondary">
                  EN TRANSIT
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowRightAlt sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Main Lab */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                border: `2px solid ${theme.palette.primary.main}`,
                background: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                    <Science sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {mainLabLocation?.name || 'Laboratoire Principal'}
                    </Typography>
                    <Chip 
                      label="MAIN LAB" 
                      size="small" 
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />

                {/* Lab Queues */}
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Hub fontSize="small" /> Files d'attente par laboratoire
                </Typography>
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {/* ICP */}
                  <Grid item xs={4}>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha('#3b82f6', 0.1), 
                      borderRadius: 2,
                      border: `1px solid ${alpha('#3b82f6', 0.3)}`,
                    }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="#3b82f6">ICP</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">{sampleCounts?.icpPrepChimique || 0}</Typography>
                          <Typography variant="caption">Prep</Typography>
                        </Box>
                        <ArrowForward sx={{ color: '#3b82f6', alignSelf: 'center' }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">{sampleCounts?.icpAnalyse || 0}</Typography>
                          <Typography variant="caption">Analyse</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  
                  {/* AU */}
                  <Grid item xs={4}>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha('#f59e0b', 0.1), 
                      borderRadius: 2,
                      border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                    }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="#f59e0b">AU (Or)</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">{sampleCounts?.auPrepChimique || 0}</Typography>
                          <Typography variant="caption">Prep</Typography>
                        </Box>
                        <ArrowForward sx={{ color: '#f59e0b', alignSelf: 'center' }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">{sampleCounts?.auAnalyse || 0}</Typography>
                          <Typography variant="caption">Analyse</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  
                  {/* SAA */}
                  <Grid item xs={4}>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha('#10b981', 0.1), 
                      borderRadius: 2,
                      border: `1px solid ${alpha('#10b981', 0.3)}`,
                    }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="#10b981">SAA</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">{sampleCounts?.saaPrepChimique || 0}</Typography>
                          <Typography variant="caption">Prep</Typography>
                        </Box>
                        <ArrowForward sx={{ color: '#10b981', alignSelf: 'center' }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">{sampleCounts?.saaAnalyse || 0}</Typography>
                          <Typography variant="caption">Analyse</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Validation & Ready */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="info.main">
                        {sampleCounts?.validation || 0}
                      </Typography>
                      <Typography variant="caption">En Validation</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {sampleCounts?.pret || 0}
                      </Typography>
                      <Typography variant="caption">Prêts</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" /> {mainLabLocation?.api_endpoint || 'Non configuré'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Transfer Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <FlightTakeoff color="primary" />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {transferStats?.totalSent || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transferts Envoyés
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                  <FlightLand color="success" />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {transferStats?.totalReceived || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transferts Reçus
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                  <Schedule color="warning" />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {(transferStats?.pendingIn || 0) + (transferStats?.pendingOut || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    En Attente
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                  <Speed color="info" />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {transferStats?.averageTransitHours ? `${transferStats.averageTransitHours}h` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Temps Moyen Transit
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transfer History */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History /> Historique des Transferts Récents
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>N° Transfert</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell align="center">Échantillons</TableCell>
                  <TableCell>De → Vers</TableCell>
                  <TableCell>Envoyé</TableCell>
                  <TableCell>Reçu</TableCell>
                  <TableCell align="center">Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Aucun transfert enregistré
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transfers.map((transfer) => (
                    <TableRow key={transfer.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                          {transfer.transfer_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transfer.batch_number || 'N/A'} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={transfer.sample_count} 
                          size="small" 
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={transfer.from_location_code} size="small" color="warning" variant="outlined" />
                          <ArrowRightAlt />
                          <Chip label={transfer.to_location_code} size="small" color="primary" variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(transfer.sent_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          par {transfer.sent_by_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {transfer.received_at ? (
                          <>
                            <Typography variant="body2">
                              {formatDate(transfer.received_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              par {transfer.received_by_name}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={transfer.status}
                          size="small"
                          color={getStatusColor(transfer.status) as any}
                          icon={transfer.status === 'RECEIVED' ? <CheckCircle /> : <Schedule />}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Locations List */}
      <Card sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn /> Sites Configurés
          </Typography>
          
          <Grid container spacing={2}>
            {locations.map((location) => (
              <Grid item xs={12} md={6} key={location.id}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    borderColor: location.type === 'MAIN_LAB' ? theme.palette.primary.main : theme.palette.warning.main,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6">{location.name}</Typography>
                      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                        {location.code}
                      </Typography>
                    </Box>
                    <Chip 
                      label={location.type} 
                      size="small" 
                      color={location.type === 'MAIN_LAB' ? 'primary' : 'warning'}
                    />
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">Capacités:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {location.capabilities?.map((cap) => (
                        <Chip key={cap} label={cap} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Endpoint: {location.api_endpoint}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MultiSiteDashboard;


